import { Inject, Injectable } from '@angular/core';
import { UAParser } from 'ua-parser-js';
import { isDefined } from '../../utilities/is-defined';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EnvironmentService } from '@evorait/pace-environment';

export const MOCK_DEVICE_ID = 'mockDeviceId';
export const DEVICE_ID_PREFIX = '[[DeviceID]]';

@Injectable({
    providedIn: 'root',
})
export class DeviceIdService {
    private readonly osId: string;
    private readonly deviceId: string;
    private readonly browserId: string;

    /**
     * This service doesnt need initialization anymore. It can be used by just injecting it and calling member functions.
     * Additionally, this service is no longer storing the device id in local storage, because it is not required
     * anymore. It will rather generate device ids on the fly, because the new device id algorithm is deterministic.
     * This service is used to support the session check and forms locking.
     *
     * @param log the log service
     * @param environmentService the environment service
     */
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        private readonly environmentService: EnvironmentService,
    ) {
        const { os, device, browser } = new UAParser().getResult();
        this.osId = this.joinParts(os?.name);
        this.deviceId = this.joinParts(device?.vendor, device?.model);
        this.browserId = this.joinParts(browser?.name);
    }

    /**
     * NEW DEVICE ID ALGORITHM.
     * The new device id is deterministic and will have the same value for a specific user, on a specific device and
     * browser. Means, the same browser on the same device for the same user will always produce the same device id.
     * Switching one condition of it will result in a different device id. Its also prefixed to make it detectable by
     * code. May throw an error if GID is empty or undefined. PACE should not continue in this case.
     *
     * Examples:
     * [[DeviceID]]-Z1234567-macOS-Apple_Macintosh-Chrome
     * [[DeviceID]]-Z1234567-macOS-Apple_Macintosh-Safari
     * [[DeviceID]]-Z1234567-iOS-Apple_iPhone-Mobile Safari
     *
     * @param gid Required to make device ids unique per user. Passed as parameter to make it testable from outside.
     * @returns The generated device id.
     */
    public getDeviceId(gid: string): string {
        if (this.environmentService.operateInMockMode) {
            return MOCK_DEVICE_ID;
        }

        if (!isDefined(gid) || gid.trim().length === 0) {
            const errorMessage = `DeviceIdService: GID may not be empty for device id generation.`;
            this.log.error(errorMessage);
            throw new Error(errorMessage);
        }

        return `${DEVICE_ID_PREFIX}-${gid}-${this.osId}-${this.deviceId}-${this.browserId}`;
    }

    /**
     * Removes empty, missing and undefined parameters and normalizes them to the word 'unknown'.
     * Used for a consistent, deterministic value generation and testability.
     *
     * @param parts Unlimited list of parameters to be normalized.
     * @private
     */
    private joinParts(...parts: Array<string | undefined>): string {
        const cleaned = parts.map((p) => p?.replace?.(/\s+/g, '') || '').filter(Boolean);
        return cleaned.join('_') || 'unknown';
    }
}
